import { Controller } from "@hotwired/stimulus";
import { countFlexRows } from "utils/flex_rows";

const LABEL_SELECTOR = ".shared--searchable-dropdown-component__label";
const CHIP_SELECTOR = ".toolkit-button-component--toolbar-chip";
const CHIP_LABEL_SELECTOR = ".toolkit-button-component__toolbar-chip-label";
const CHIP_ICON_SELECTOR = "svg, i, img";
const CHIP_STATE_ATTR = "data-chip-labels";
const BINARY_SEARCH_PRECISION_PX = 8;

/**
 * Keeps the AG-Grid toolbar on as few wrapped rows as possible, degrading in two stages and
 * keeping a stage only when it actually removes a row: clip nav-dropdown labels, then collapse
 * chips to icon-only. Clipping first, because an ellipsis still reads and a bare icon does not.
 *
 * Neither CSS tool can do this. Breakpoints can't: the fit is the sum of every sibling's width,
 * which varies per page, not viewport width. Flexbox can't either: fixing line-assignment needs a
 * 0% flex-basis, but flex-grow then reinflates the item exactly when it is crowding others out.
 *
 * Below the bar's --ag-toolbar-stacked breakpoint CSS owns the row layout, so stage 1 stands down
 * there; chips this never reaches keep button_component.scss's viewport fallback.
 */
export default class extends Controller {
  static values = {
    minLabelWidth: { type: Number, default: 72 },
  };

  declare readonly minLabelWidthValue: number;

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private pendingFrame: number | null = null;
  private lastWidth = -1;
  private applying = false;

  connect() {
    const bar = this.barElement;

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(bar);

    this.mutationObserver = new MutationObserver(() => {
      if (this.applying) return;
      this.scheduleRecompute();
    });
    this.mutationObserver.observe(bar, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "hidden", "class"],
    });

    this.scheduleRecompute();
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }
  }

  private get barElement(): HTMLElement {
    return this.element as HTMLElement;
  }

  // Width only: our own clipping changes the bar's HEIGHT, which would re-fire this forever.
  private handleResize() {
    const width = this.barElement.clientWidth;
    if (width === this.lastWidth) return;

    this.lastWidth = width;
    this.scheduleRecompute();
  }

  // Coalesces every trigger (resize + mutation) through a single rAF so the
  // binary search's forced-layout reads run at most once per frame.
  private scheduleRecompute() {
    if (this.pendingFrame !== null) cancelAnimationFrame(this.pendingFrame);

    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.recompute();
    });
  }

  private recompute() {
    const bar = this.barElement;
    const labels = this.managedLabels(bar);
    const chips = this.collapsibleChips(bar);
    const canClip = labels.length > 0 && !this.isStacked(bar);

    // Always measure from the fully-expanded state, so a toolbar that has since gained
    // room (window widened, a chip hidden) gets its words back.
    this.applyMaxWidth(labels, null);
    this.setChipsCollapsed(chips, false);

    const items = this.effectiveFlexItems(bar);
    if (this.countRows(items) <= 1) return;

    if (canClip && this.clipLabels(labels, items) <= 1) return;
    if (chips.length === 0) return;

    const rowsBeforeCollapse = this.countRows(items);
    this.setChipsCollapsed(chips, true);
    if (this.countRows(items) >= rowsBeforeCollapse) {
      // Icon-only bought us nothing, so the words stay.
      this.setChipsCollapsed(chips, false);
      return;
    }

    // Collapsing freed horizontal space; re-run the search to hand back as much of the
    // label as the new layout allows, rather than paying for both degradations.
    if (canClip) this.clipLabels(labels, items);
  }

  // Clips `labels` to the widest max-width that minimises the toolbar's row count, leaving
  // them unclipped when clipping wouldn't reduce it. Returns the resulting row count.
  private clipLabels(labels: HTMLElement[], items: HTMLElement[]): number {
    this.applyMaxWidth(labels, null);
    const baseRows = this.countRows(items);
    if (baseRows <= 1) return baseRows;

    // offsetWidth, not scrollWidth: it respects the stylesheet's max-width cap,
    // so we never widen a label past what CSS alone would have rendered.
    const naturalWidth = Math.max(...labels.map((label) => label.offsetWidth));

    this.applyMaxWidth(labels, this.minLabelWidthValue);
    const minRows = this.countRows(items);
    if (minRows >= baseRows) {
      // Minimum-width clipping didn't reduce the row count, so the wrap has some other
      // cause -- clipping would be pointless.
      this.applyMaxWidth(labels, null);
      return baseRows;
    }

    const bestWidth = this.largestWidthForRowCount(
      labels,
      items,
      minRows,
      this.minLabelWidthValue,
      naturalWidth
    );
    this.applyMaxWidth(labels, bestWidth);

    return minRows;
  }

  // Binary-searches the largest max-width in [low, high] that still yields
  // `targetRows` wrapped rows, to sub-8px precision.
  private largestWidthForRowCount(
    labels: HTMLElement[],
    items: HTMLElement[],
    targetRows: number,
    low: number,
    high: number
  ): number {
    let lo = low;
    let hi = high;
    let best = low;

    while (hi - lo >= BINARY_SEARCH_PRECISION_PX) {
      const mid = Math.round((lo + hi) / 2);
      this.applyMaxWidth(labels, mid);

      if (this.countRows(items) === targetRows) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }

    return best;
  }

  private applyMaxWidth(labels: HTMLElement[], width: number | null) {
    this.applying = true;
    labels.forEach((label) => {
      if (width === null) {
        label.style.removeProperty("max-width");
      } else {
        label.style.maxWidth = `${width}px`;
      }
    });
    // Drain records generated by the writes above so the mutation observer's
    // callback never fires for changes we caused ourselves.
    this.mutationObserver?.takeRecords();
    this.applying = false;
  }

  private managedLabels(bar: HTMLElement): HTMLElement[] {
    return Array.from(bar.querySelectorAll<HTMLElement>(LABEL_SELECTOR));
  }

  // An icon-less chip would collapse to an empty box, so it keeps its words whatever the
  // row count says.
  private collapsibleChips(bar: HTMLElement): HTMLElement[] {
    return Array.from(bar.querySelectorAll<HTMLElement>(CHIP_SELECTOR)).filter(
      (chip) =>
        chip.querySelector(CHIP_LABEL_SELECTOR) !== null &&
        chip.querySelector(CHIP_ICON_SELECTOR) !== null
    );
  }

  // Stamped even when expanded: the attribute's absence is what tells the stylesheet a chip
  // was never measured and should fall back to the viewport breakpoint.
  private setChipsCollapsed(chips: HTMLElement[], collapsed: boolean) {
    this.applying = true;
    chips.forEach((chip) => {
      chip.setAttribute(CHIP_STATE_ATTR, collapsed ? "collapsed" : "expanded");
    });
    this.mutationObserver?.takeRecords();
    this.applying = false;
  }

  private isStacked(bar: HTMLElement): boolean {
    return getComputedStyle(bar).getPropertyValue("--ag-toolbar-stacked").trim() === "1";
  }

  private isRendered(el: HTMLElement): boolean {
    if (getComputedStyle(el).display === "none" || el.hidden) return false;

    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // The bar's real flex participants: `display: contents` wrappers are transparent to
  // flex layout, so their children -- not the wrapper -- are the actual flex items.
  private effectiveFlexItems(bar: HTMLElement): HTMLElement[] {
    const items: HTMLElement[] = [];

    const visit = (parent: HTMLElement) => {
      Array.from(parent.children).forEach((child) => {
        const el = child as HTMLElement;
        // Must precede the isRendered check: a display:contents element generates
        // no box, so its rect is 0x0 and isRendered would discard it unrecursed.
        if (getComputedStyle(el).display === "contents") {
          visit(el);
        } else if (this.isRendered(el)) {
          items.push(el);
        }
      });
    };

    visit(bar);
    return items;
  }

  private countRows(items: HTMLElement[]): number {
    return countFlexRows(items);
  }
}
